import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteProps, AutocompleteRenderOptionState, Chip, createFilterOptions, TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";
import Api from "../../../../../core/services/api/api";

/**
 * Interface representing a PartSupportReason option in the autocomplete dropdown.
 * This structure matches the API response format for PartSupportReason data.
 *
 * @property id - Unique identifier for the PartSupportReason
 * @property name - Display name of the PartSupportReason
 * @property city - City where the PartSupportReason is located
 */
interface PartSupportReasonOption {
    id: number;
    title?: string;
    code?: string;
}

/**
 * Props interface for the PartSupportReasonAutocomplete component.
 * Extends the base autocomplete functionality with PartSupportReason-specific features.
 *
 * @property textFieldProps - Additional props to pass to the TextField component
 * @property form - Whether the component is used within a form
 * @property name - Name attribute for form integration
 * @property required - Whether the field is required in forms
 * @property setLoadingOptions - Callback to handle loading state changes
 * @property value - Currently selected PartSupportReason option
 * @property onChange - Callback fired when selection changes
 * @property renderOption - Custom render function for dropdown options
 */
interface PartSupportReasonAutocompleteProps<M extends boolean = false> extends Omit<AutocompleteProps<PartSupportReasonOption, M, false, false>, 'renderInput' | 'renderOption' | 'options'> {
    statusId?: number;
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;
    renderOption?: (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: PartSupportReasonOption,
        state: AutocompleteRenderOptionState,
        defaultRender: (props: React.HTMLAttributes<HTMLLIElement>, option: PartSupportReasonOption) => React.ReactNode
    ) => React.ReactNode;

    [key: string]: any;
}

/**
 * PartSupportReasonAutocomplete Component
 *
 * A searchable dropdown component for selecting PartSupportReasons with the following features:
 * - Asynchronous search with debouncing
 * - Custom option rendering with name and city display
 * - Loading state management
 * - Form integration support
 * - Customizable styling and behavior
 *
 * @component
 */
const PartSupportReasonAutocomplete = <M extends boolean = false, >({
                                                                        textFieldProps = {},
                                                                        form = false,
                                                                        name = undefined,
                                                                        required = false,
                                                                        setLoadingOptions: setLoadingOptionsProps,
                                                                        renderOption: customRenderOption,
                                                                        statusId,
                                                                        ...props
                                                                    }: PartSupportReasonAutocompleteProps<M>) => {
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    /**
     * Syncs the internal loading state with the parent component
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * Fetches PartSupportReason options based on search keywords
     * Handles loading state and component unmounting
     *
     * @param cancelToken - Axios cancel token for request cancellation
     * @param keywords - Search query string
     * @returns Array of matching PartSupportReason options
     */
    const getOptions = useCallback(async (cancelToken: CancelToken) => {
        setLoadingOptions(true);
        try {
            // @ts-expect-error Api method exists but is not yet typed
            const response = (await Api.getPartSupportTypesAndStatuses(cancelToken));
            if (!isMounted())
                return [];
            return (response?.data?.statuses ?? [])?.reduce((acc: PartSupportReasonOption[], status: { id: number; title?: string, reasons?: PartSupportReasonOption[] }) => {
                if (status.reasons?.length && (!statusId || status.id === statusId)) {
                    acc.push(...status.reasons);
                }
                return acc;
            }, [] as PartSupportReasonOption[]);
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [isMounted, statusId]);

    /**
     * Determines if two PartSupportReason options are equal based on their IDs
     */
    const isOptionEqualToValue = useCallback((option: PartSupportReasonOption, value: PartSupportReasonOption | null) =>
        option.id === value?.id || (option.code === value?.code && option.code != null), []);

    /**
     * Extracts the display label from a PartSupportReason option
     */
    const getOptionLabel = useCallback((option: PartSupportReasonOption) => `${option.code ? `(${option.code}) - ` : ""}${option.title ?? ""}`, []);

    /**
     * Gets the unique key for a PartSupportReason option
     */
    const getOptionKey = useCallback((option: PartSupportReasonOption) => option?.id ?? ((option.title != null) ? `title_${option.title}` : null) ?? "", []);

    // GetOptions will populate an initial empty array
    const defaultOptions = useMemo(() => [], []);

    const filterOptions = useMemo(() => createFilterOptions<PartSupportReasonOption>({
        stringify: (option) => `${option.code ? `(${option.code}) - ` : ""}${option.title ?? ""}`,
    }), []);

    /**
     * Renders selected PartSupportReason options as chips within the input field
     */
    const renderTags = useCallback((value: PartSupportReasonOption[], getTagProps: ({index}: { index: number }) => {}) =>
        value.map((option: PartSupportReasonOption, index: number) => (
            <Chip
                key={getOptionKey(option)}
                label={option.code ?? ""}
                {...getTagProps({index})}
            />
        )), [getOptionKey]);

    return (
        <AutocompleteBase<PartSupportReasonOption, M>
            canSearchEmptyKeywords
            keywordsBased={false}
            getOptions={getOptions}
            timeout={1000}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            options={defaultOptions}
            clearOnBlur={false}
            filterOptions={filterOptions}
            renderTags={renderTags}
            {...props}
        />
    );
};

export default PartSupportReasonAutocomplete;